import React, { useState } from 'react';
import { Button } from '../../components/ui';
import { authFetch } from '../../lib/api/authFetch';
import { API_BASE } from '../../lib/api/version';

interface Props {
  projectId: string | null;
  onSaved: () => void;
  onSkip?: () => void;
  onBack?: () => void;
  /**
   * UX-08: rendered inside StepGithub's collapsed "Add your own API keys
   * (optional)" section instead of as its own wizard step. Embedded mode
   * has no Back/Skip/Continue of its own — saving confirms inline and
   * calls `onSaved` right away (telemetry only; it no longer advances
   * the wizard).
   */
  embedded?: boolean;
}

type KeyState = 'idle' | 'saving' | 'saved' | 'error' | 'invalid';

export const StepByok: React.FC<Props> = ({ projectId, onSaved, onSkip, onBack, embedded }) => {
  const [anthropicKey, setAnthropicKey] = useState('');
  const [keyState, setKeyState] = useState<KeyState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSave = async () => {
    if (!anthropicKey.trim() || !projectId) return;
    setKeyState('saving');
    setErrorMsg('');
    try {
      const r = await authFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/byok`, {
        method: 'POST',
        body: JSON.stringify({ provider: 'anthropic', secret: anthropicKey.trim() }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: { message?: string } };
        setErrorMsg(j?.error?.message ?? 'Failed to save key');
        setKeyState('error');
        return;
      }
      const data = (await r.json()) as { data?: { keyValid?: boolean } };
      if (data.data?.keyValid === false) {
        setKeyState('invalid');
        setErrorMsg(
          embedded
            ? 'Key saved but validation failed — verify the key and try again.'
            : 'Key saved but validation failed — verify the key and try again, or continue anyway.',
        );
      } else {
        setKeyState('saved');
      }
      // Embedded: there's no separate "Continue" to gate this behind — the
      // save attempt itself is the moment to record the telemetry.
      if (embedded) onSaved();
    } catch (err) {
      setErrorMsg((err as Error).message);
      setKeyState('error');
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        Forge uses your Anthropic API key to run agents. The key is stored encrypted in AWS Secrets Manager and never exposed in plaintext after this step.
      </p>

      {keyState === 'saved' ? (
        <div className="flex items-center gap-2 text-sm text-success-700 bg-success-50 border border-success-200 rounded-lg px-4 py-3">
          <span>✓</span> Anthropic API key configured
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">
            Anthropic API key <span className="text-danger-600">*</span>
          </label>
          <input
            type="password"
            value={anthropicKey}
            onChange={(e) => setAnthropicKey(e.target.value)}
            placeholder="sk-ant-…"
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
            autoComplete="off"
          />
          {(keyState === 'error' || keyState === 'invalid') && (
            <p className="text-xs text-danger-700">{errorMsg}</p>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSave}
            loading={keyState === 'saving'}
            disabled={!anthropicKey.trim() || !projectId}
          >
            {keyState === 'saving' ? 'Validating & saving…' : 'Save key'}
          </Button>
        </div>
      )}

      {!embedded && (
        <div className="flex gap-3 pt-2">
          <Button variant="ghost" size="md" onClick={onBack} className="flex-1">Back</Button>
          <Button variant="ghost" size="md" onClick={onSkip} className="flex-1">Skip for now</Button>
          {(keyState === 'saved' || keyState === 'invalid') ? (
            <Button variant="primary" size="md" onClick={onSaved} className="flex-1">Continue</Button>
          ) : null}
        </div>
      )}
    </div>
  );
};
